import { KeyVals, map, forEachIterator, clip } from '../../../../common/src/ts/utils';
import { html as HTML_NS, ssml as SSML_NS, svg as SVG_NS } from '../../../../common/src/ts/xmlns';
import {
  isElement,
  isText,
  iterateChildNodes,
  isDocument,
  createSingleNodeRange,
  createPointRange,
} from '../../../../common/src/ts/utils/dom';
import Trie from '../utils/Trie';
import {
  MultiStringView,
  StringView,
  SingleStringView,
} from '../utils/StringView';
import jeitaIt4006 from './jeita-it-4006';
import { SpeechText, SpeechItem } from './SpeechText';
import { VOICE_NEWLINE_RECOGNISABLE } from './env';

export interface NewlineTag {
  readonly name: string;
  readonly namespaceURI: string;
}

interface ElementLocalNameSet {
  [name: string]: boolean;
}

const INLINE_TAGS: ElementLocalNameSet = {
  a: true,
  abbr: true,
  b: true,
  bdi: true,
  bdo: true,
  br: true,
  cite: true,
  code: true,
  data: true,
  dfn: true,
  em: true,
  i: true,
  kbd: true,
  mark: true,
  nobr: true,
  q: true,
  rb: true,
  rp: true,
  rt: true,
  rtc: true,
  ruby: true,
  s: true,
  samp: true,
  small: true,
  span: true,
  strong: true,
  sub: true,
  sup: true,
  time: true,
  tt: true,
  u: true,
  var: true,
  wbr: true,
};

const IGNORE_TAGS: ElementLocalNameSet = {
  head: true,
  style: true,
  script: true,
  template: true,
};

export type Dictionary = KeyVals<string>;

export interface BuilderOptions {
  dictionary?: Readonly<Dictionary>;
}

function rubyToSpheechText(ruby: Element) {
  const rts = ruby.querySelectorAll('rt');
  return map(rts, rt => rt.textContent!.trim().replace(/[\r\n]/, ' ')).join('');
}

function svgRubyToSpheechText(ruby: Element) {
  const rts = ruby.querySelectorAll('.ruby-text');
  return map(rts, rt => rt.textContent!.trim().replace(/[\r\n]/, ' ')).join('');
}

function phToSpheechText(ph: string, _alphabet: 'x-JEITA' = 'x-JEITA') {
  const result:string[] = [];
  let current: StringView = new SingleStringView(ph);
  while (current.length !== 0) {
    const match = jeitaIt4006.longestMatch(current);
    if (match === undefined) {
      current = current.slice(1);
      continue;
    }
    current = match[0];
    result.push(match[1]);
  }
  return result.join('');
}

const enum Type {
  REPLACEABLE_TEXT,
  FIXED_TEXT,
  SPACE,
  BREAK,
}

interface EntryBase {
  type: Type;
  range: Range;
}
interface SpaceEntry extends EntryBase {
  type: Type.SPACE;
}

interface BreakEntry extends EntryBase {
  type: Type.BREAK;
}

interface ReplaceableTextEntry extends EntryBase {
  type: Type.REPLACEABLE_TEXT;
}

interface FixedTextEntry extends EntryBase {
  type: Type.FIXED_TEXT;
  text: string;
}

type TextEntry = ReplaceableTextEntry | FixedTextEntry;

type Entry = SpaceEntry | BreakEntry | TextEntry;

function trim(text: Text): [number, number] {
  const textContent = text.textContent!;
  const s = text.previousSibling !== null ? 0 :  /^[\r\n\t ]*/.exec(textContent)!.index;
  const e = text.nextSibling !== null ? textContent.length : /[\r\n\t ]*$/.exec(textContent)!.index;
  return [s, Math.max(e, s)];
}

function createEntry(range: Range, isSpace: boolean): ReplaceableTextEntry | SpaceEntry {
  return isSpace ? { range, type: Type.SPACE } : { range, type : Type.REPLACEABLE_TEXT };
}

function* splitTextNode(node: Text,
                        start?: number,
                        end?: number) : Iterator<ReplaceableTextEntry | SpaceEntry> {
  const textContent = node.textContent!;
  const [startSkip, endSkip] = trim(node);
  let i = clip(start === undefined ? 0 : start, 0, textContent.length, startSkip, endSkip);
  const e = clip(end === undefined ? 0 : end, 0, textContent.length, i, endSkip);
  if (i === e) {
    return;
  }
  let isSpace = false;
  let prev = i;
  while (i < e) {
    const currentIsSpace = /[ \r\n\t]/.test(textContent[i]);
    if (i !== prev) {
      if (isSpace !== currentIsSpace) {
        yield createEntry(createSingleNodeRange(node, prev, i), isSpace);
        prev = i;
      }
    }
    isSpace = currentIsSpace;
    i += 1;
  }
  yield createEntry(createSingleNodeRange(node, prev, e), isSpace);
}

function joinEntry(entry1: SpaceEntry | BreakEntry,
                   entry2: SpaceEntry | BreakEntry): SpaceEntry | BreakEntry {
  const range = entry1.range.cloneRange();
  const { endContainer, endOffset } = entry2.range;
  range.setEnd(endContainer, endOffset);
  if (entry1.type === Type.SPACE && entry2.type === Type.SPACE) {
    return { range, type: Type.SPACE };
  }
  return { range, type: Type.BREAK };
}

export class SpeechTextBuilder {
  private entries: Entry[] = [];
  private dictionary: Trie<string>;

  public constructor(options: BuilderOptions = {}) {
    const dictionary = options.dictionary !== undefined ? options.dictionary : {};
    this.dictionary = new Trie<string>(dictionary);
  }

  public pushTextNode(node: Text): void;
  public pushTextNode(node: Text, start: number, end: number): void;
  public pushTextNode(node: Text, start?: number, end?: number) {
    forEachIterator(splitTextNode(node, start, end), (entry) => {
      if (entry.type === Type.SPACE) {
        this.pushSpace(entry);
      } else {
        this.entries.push(entry);
      }
    });
  }

  private pushSpace(entry: SpaceEntry) {
    const { entries } = this;
    const { length } = entries;
    if (length !== 0) {
      const prev = entries[length - 1];
      if (prev.type === Type.SPACE || prev.type === Type.BREAK) {
        entries[length - 1] = joinEntry(prev, entry);
        return;
      }
      entries.push(entry);
    }
  }

  private pushBreak(entry: BreakEntry) {
    const { entries } = this;
    const { length } = entries;
    if (length !== 0) {
      const prev = entries[length - 1];
      if (prev.type === Type.SPACE || prev.type === Type.BREAK) {
        entries[length - 1] = joinEntry(prev, entry);
        return;
      }
      entries.push(entry);
    }
  }

  public pushElement(element: Element, clipped?: Range) {
    if (element.tagName in IGNORE_TAGS) {
      return;
    }
    if (element.ownerDocument === null) {
      return;
    }

    const needBreak = !(element.namespaceURI === HTML_NS && element.nodeName in INLINE_TAGS)
                      && !(element.namespaceURI === SVG_NS && element.nodeName !== 'text');
    if (needBreak) {
      this.pushBreak({ type: Type.BREAK, range: createPointRange(element) });
    }
    if (element.hasAttributeNS(SSML_NS, 'ph')) {
      const phAttribute = element.getAttributeNS(SSML_NS, 'ph');
      if (phAttribute !== null) {
        const text = phToSpheechText(phAttribute);
        const range = element.ownerDocument.createRange();
        range.selectNode(element);
        this.entries.push({ text, range, type: Type.FIXED_TEXT });
      }
    } else if (element.namespaceURI === HTML_NS && element.nodeName === 'ruby') {
      const text = rubyToSpheechText(element);
      const range = element.ownerDocument.createRange();
      range.selectNode(element);
      this.entries.push({ text, range, type: Type.FIXED_TEXT });
    } else if (element.namespaceURI === SVG_NS && element.classList.contains('ruby')) {
      const text = svgRubyToSpheechText(element);
      const range = element.ownerDocument.createRange();
      range.selectNode(element);
      this.entries.push({ text, range, type: Type.FIXED_TEXT });
    } else {
      forEachIterator(
        iterateChildNodes(element, clipped),
        (node) => {
          if (isElement(node)) {
            this.pushElement(node, clipped);
          } else if (isText(node)) {
            if (clipped !== undefined) {
              const { startContainer, startOffset, endContainer, endOffset } = clipped;
              this.pushTextNode(node,
                                startContainer === node ? startOffset : 0,
                                endContainer === node ? endOffset : node.length);
            } else {
              this.pushTextNode(node, 0, node.length);
            }
          }
        },
      );
    }
    if (needBreak) {
      this.pushBreak({
        type: Type.BREAK,
        range: createPointRange(element, element.childNodes.length),
      });
    }
  }

  public setRange(range: Range) {
    this.clearEntries();
    const { startContainer, startOffset, endContainer, endOffset } = range;
    const document = isDocument(startContainer) ? startContainer : startContainer.ownerDocument;
    if (document === null) {
      return;
    }

    forEachIterator(
      iterateChildNodes(document, range),
      (node) => {
        if (isElement(node)) {
          this.pushElement(node, range);
        }
        if (isText(node)) {
          this.pushTextNode(node,
                            startContainer === node ? startOffset : 0,
                            endContainer === node ? endOffset : node.length);
        }
      },
    );
  }

  public clearEntries() {
    this.entries = [];
  }

  private *buildSupport(): Iterator<Entry> {
    const { entries } = this;
    for (let i = 0; i < entries.length;) {
      for (; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.type === Type.REPLACEABLE_TEXT || entry.type === Type.SPACE) {
          break;
        }
        yield entry;
      }
      const view = new MultiStringView();
      const mappings: [number, ReplaceableTextEntry | SpaceEntry][] = [];
      for (; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.type === Type.FIXED_TEXT || entry.type === Type.BREAK) {
          break;
        }
        mappings.push([view.length, entry]);
        if (entry.type === Type.SPACE) {
          view.append(' ');
        } else {
          const { startContainer, startOffset, endOffset } = entry.range;
          const text = startContainer.textContent!.slice(startOffset, endOffset);
          view.append(text);
        }
      }
      let mapIndex = 0;
      for (let current : StringView = view; current.length > 0;) {
        const match = this.dictionary.longestMatch(current);
        if (match === undefined) {
          current = current.slice(1);
          continue;
        }
        const [next, text] = match;
        const currentOffset = view.length - current.length;
        const nextOffset = view.length - next.length;
        for (;; mapIndex += 1) {
          const [offset, entry] = mappings[mapIndex];
          if (entry.type === Type.SPACE) {
            if (offset === nextOffset) {
              break;
            }
            yield entry;
            continue;
          }
          const { startContainer, startOffset, endOffset } = entry.range;
          const length = endOffset - startOffset;
          if (offset === currentOffset) {
            break;
          }
          if (offset + length <= currentOffset) {
            yield entry;
            continue;
          }
          const range = entry.range.cloneRange();
          range.setEnd(startContainer, startOffset + currentOffset - offset);
          yield { range, type: Type.REPLACEABLE_TEXT };
          const newRange = entry.range.cloneRange();
          newRange.setStart(startContainer, startOffset + currentOffset - offset);
          mappings[mapIndex] = [currentOffset, { range: newRange, type: Type.REPLACEABLE_TEXT }];
          break;
        }
        const range = mappings[mapIndex][1].range.cloneRange();
        for (;; mapIndex += 1) {
          const [offset, entry] = mappings[mapIndex];
          if (entry.type === Type.SPACE) {
            if (offset + 1 === nextOffset) {
              range.setEnd(entry.range.endContainer, entry.range.endOffset);
              break;
            }
            continue;
          }
          const { startContainer, startOffset, endOffset } = entry.range;
          const length = endOffset - startOffset;
          if (offset + length === nextOffset) {
            range.setEnd(entry.range.endContainer, entry.range.endOffset);
            break;
          }
          if (offset + length <= nextOffset) {
            continue;
          }
          range.setEnd(startContainer, startOffset + nextOffset - offset);
          const newRange = entry.range.cloneRange();
          newRange.setStart(startContainer, startOffset + nextOffset - offset);
          mappings[mapIndex] = [nextOffset, { range: newRange, type: Type.REPLACEABLE_TEXT }];
          break;
        }
        yield { range, text, type: Type.FIXED_TEXT };
        current = next;
      }
      while (mapIndex < mappings.length) {
        yield mappings[mapIndex][1];
        mapIndex += 1;
      }
    }
  }

  public build() {
    let currentItem: SpeechItem = { text: '', mappings: [] };
    const result: SpeechItem[] = [currentItem];
    forEachIterator(this.buildSupport(), (entry) => {
      switch (entry.type) {
        case Type.BREAK:
          if (VOICE_NEWLINE_RECOGNISABLE) {
            currentItem.mappings.push({
              index: currentItem.text.length,
              length: 1,
              range: entry.range,
              replaced: true,
            });
            currentItem.text += '\n';
          } else {
            currentItem = { text: '', mappings: [] };
            result.push(currentItem);
          }
          break;
        case Type.FIXED_TEXT:
          currentItem.mappings.push({
            index: currentItem.text.length,
            length: entry.text.length,
            range: entry.range,
            replaced: true,
          });
          currentItem.text += entry.text;
          break;
        case Type.REPLACEABLE_TEXT: {
          const { startContainer, startOffset, endOffset } = entry.range;
          const text = startContainer.textContent!.slice(startOffset, endOffset);
          currentItem.mappings.push({
            index: currentItem.text.length,
            length: text.length,
            range: entry.range,
            replaced: false,
          });
          currentItem.text += text;
        }
      }
    });
    return new SpeechText(result);
  }
}
